<template>
  <v-container class="asc-email-subscriptions--container pa-0">
    <template v-if="currentSubscriptions.length">
      <v-row no-gutters>
        <v-col cols="12">
          <asc-accordion class="asc-email-subscriptions--accordion">
            <asc-accordion-item
              v-for="(item, index) in currentSubscriptions"
              :id="item.emailType"
              :key="item.id"
              class="pa-1"
              :hide-actions="!item.filterable"
              :readonly="!item.filterable"
            >
              <template slot="header">
                <v-row :class="[!item.filterable ? 'mr-2' : null]" no-gutters>
                  <v-col cols="12" md="6" sm="9" @click.prevent.stop>
                    <asc-autocomplete-selector
                      v-model="item.emailTypeId"
                      label="Email Subscriptions"
                      :items="getEmailSubTypes(item.emailTypeId)"
                      dense
                      short
                      no-array
                      :disabled="isUserReader"
                      @input="handleSubTypeSelection(index, ...arguments)"
                    ></asc-autocomplete-selector>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-col
                    cols="12"
                    md="2"
                    sm="3"
                    class="text-right align-self-center"
                  >
                    <asc-accent-button
                      :class="[
                        'asc-email-subscriptions--accordion-header-button',
                        !item.filterable
                          ? 'asc-email-subscriptions--accordion-header--noDropdown'
                          : null,
                        'mr-3',
                      ]"
                      button-text="Remove"
                      icon="mdi-delete"
                      icon-position="left"
                      secondary
                      :value="item"
                      :on-click="handleSubscriptionRemove"
                      :disabled="isUserReader"
                    ></asc-accent-button>
                  </v-col>
                </v-row>
              </template>
              <template v-if="item.filterable">
                <v-row
                  class="align-content-start mr-3 mt-1 subscriptionFilters"
                >
                  <v-col
                    v-if="subscriptionHasFilter(item.emailType, 'AirlineId')"
                    cols="12"
                    md="6"
                  >
                    <asc-autocomplete-selector
                      :id="`${index}_airline`"
                      v-model="item.filter.AirlineId"
                      label="Airlines"
                      :placeholder="
                        item.filter.AirlineId && item.filter.AirlineId.length
                          ? ''
                          : 'All'
                      "
                      :items="getAirlinesOptions"
                      component-type="multiple"
                      display-clear
                      fixed-position
                      dense
                      short
                    ></asc-autocomplete-selector>
                  </v-col>

                  <v-col
                    v-if="
                      subscriptionHasFilter(item.emailType, 'MasterModelId')
                    "
                    cols="12"
                    md="6"
                  >
                    <asc-autocomplete-selector
                      :id="`${index}_masterModel`"
                      v-model="item.filter.MasterModelId"
                      label="Platforms"
                      :placeholder="
                        item.filter.MasterModelId &&
                        item.filter.MasterModelId.length
                          ? ''
                          : 'All'
                      "
                      :items="getMasterModelItems(item.filter.AirlineId)"
                      component-type="multiple"
                      display-clear
                      fixed-position
                      dense
                      short
                    ></asc-autocomplete-selector>
                  </v-col>
                  <v-col
                    v-if="subscriptionHasFilter(item.emailType, 'AnalyticId')"
                    cols="12"
                    md="6"
                  >
                    <asc-autocomplete-selector
                      :id="`${index}_analytic`"
                      v-model="item.filter.AnalyticId"
                      label="Associated Analytic"
                      :placeholder="
                        item.filter.AnalyticId && item.filter.AnalyticId.length
                          ? ''
                          : 'All'
                      "
                      :items="
                        getAnalyticModulesItems(item.filter.MasterModelId)
                      "
                      component-type="multiple"
                      display-clear
                      fixed-position
                      dense
                      short
                    ></asc-autocomplete-selector>
                  </v-col>
                  <v-col
                    v-if="subscriptionHasFilter(item.emailType, 'ReportNumber')"
                    cols="12"
                    md="6"
                  >
                    <asc-autocomplete-selector
                      :id="`${index}_reportNumber`"
                      v-model="item.filter.ReportNumber"
                      label="Report Types"
                      :placeholder="
                        item.filter.ReportNumber &&
                        item.filter.ReportNumber.length
                          ? ''
                          : 'All'
                      "
                      :items="getReportsOptions"
                      component-type="multiple"
                      display-clear
                      fixed-position
                      dense
                      short
                    ></asc-autocomplete-selector>
                  </v-col>
                </v-row>
              </template>
            </asc-accordion-item>
          </asc-accordion>
        </v-col>
      </v-row>
    </template>
    <v-row no-gutters>
      <v-col class="text-right py-2" cols="12">
        <asc-accent-button
          :disabled="isUserReader || addNewDisabled"
          button-text="Add Subscription"
          icon="mdi-plus"
          icon-position="left"
          :on-click="handleAddNewSubscription"
        ></asc-accent-button>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AscAutocompleteSelector from './AscAutocompleteSelector'
import AscAccordion from '@/components/atoms/AscAccordion'
import AscAccordionItem from '@/components/atoms/AscAccordionItem'
import AscAccentButton from '@/components/atoms/AscAccentButton'

export default {
  name: 'AscEmailSubscriptionsTab',

  components: {
    AscAutocompleteSelector,
    AscAccordion,
    AscAccordionItem,
    AscAccentButton,
  },

  props: {
    airlines: {
      type: Array,
      default: () => [],
    },
    masterModels: {
      type: Array,
      default: () => [],
    },
    analyticModules: {
      type: Array,
      default: () => [],
    },
    reportTypes: {
      type: Array,
      default: () => [],
    },
    emailSubscriptionTypes: {
      type: Array,
      default: () => [],
    },
    userEmailSubscriptions: {
      type: Object,
      default: () => {},
    },
    user: {
      type: Object,
      default: () => {},
    },
    isUserReader: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentSubscriptions: [],
      addNewDisabled: false,
      subsWithReport: [
        'Report_Email',
        'Parameter_Exceedance_Alert_Email',
        'Analytic_Alert_Email',
      ],
    }
  },

  computed: {
    getAirlinesOptions() {
      if (this.airlines) {
        return this.airlines.map(item => ({
          text: item.name,
          value: item.id,
        }))
      }
      return []
    },
    getReportsOptions() {
      if (this.reportTypes) {
        return this.reportTypes.map(item => ({
          text: item.displayName,
          value: item.id,
        }))
      }
      return []
    },
  },

  watch: {
    userEmailSubscriptions: {
      immediate: true,
      handler(newVal) {
        // Validate initally on load
        this.setCurrentSubscriptions(newVal)
        this.validateSubscriptions()
      },
    },
    currentSubscriptions: {
      deep: true,
      handler() {
        // Validate on every currentSubscriptions change
        this.validateSubscriptions()
        this.currentSubscriptionsChanged()

        if (
          this.currentSubscriptions.length >= this.emailSubscriptionTypes.length
        ) {
          this.addNewDisabled = true
        } else this.addNewDisabled = false
      },
    },
  },

  methods: {
    getEmailSubTypes(emailTypeId) {
      const currentSubscriptionsTypes = this.currentSubscriptions.map(
        est => est.emailTypeId,
      )
      const emailTypeOptions = this.emailSubscriptionTypes.filter(emailSub => {
        if (emailSub.id === emailTypeId) return true
        else return currentSubscriptionsTypes.indexOf(emailSub.id) === -1
      })

      return emailTypeOptions.map(eto => ({
        text: this.formatSubNames(eto.name),
        value: eto.id,
      }))
    },
    getMasterModelItems(airlineIds = []) {
      return airlineIds
        ? this.masterModels.filter(function (masterModel) {
            return airlineIds.includes(masterModel.airlineId)
          })
        : []
    },
    getAnalyticModulesItems(masterModelIds = []) {
      return masterModelIds
        ? this.analyticModules.filter(function (masterModel) {
            return masterModelIds.includes(masterModel.masterModelId)
          })
        : []
    },
    subscriptionHasFilter(emailType, filterType) {
      if (this.subsWithReport.includes(emailType)) {
        if (['AirlineId', 'ReportNumber'].includes(filterType)) {
          return true
        }
        return false
      } else if (filterType === 'ReportNumber') {
        return false
      }

      return true
    },
    setCurrentSubscriptions(userEmailSubscriptions) {
      const userEmailLists =
        this.user && this.user.userId
          ? userEmailSubscriptions[this.user.userId]
          : []

      this.currentSubscriptions =
        userEmailLists && userEmailLists.length
          ? userEmailLists.map(em => {
              const selectedEmailsubType = this.emailSubscriptionTypes.find(
                emailSub => emailSub.id === em.emailTypeId,
              )

              // Report_Email, Parameter_Exceedance_Email,Analytic_Alert_Email
              // will have AirlineId [ ] and ReportNumber [ ] only

              // Rest of the subscription types with filterable true will have
              // AirlineId [], AnalyticId [ ], MasterModelId [ ]

              if (selectedEmailsubType) {
                return {
                  id: em.id,
                  userId: em.userId,
                  emailTypeId: selectedEmailsubType.id,
                  emailType: selectedEmailsubType.name,
                  name: this.formatSubNames(selectedEmailsubType.name),
                  filterable: selectedEmailsubType.filterable,
                  filter: em.filter,
                }
              } else return null
            })
          : []
    },
    handleAddNewSubscription() {
      this.currentSubscriptions.push({
        userId: this.user.userId,
        emailTypeId: '',
        emailType: '',
        name: '',
        filterable: false,
        filter: {},
      })
    },
    handleSubTypeSelection(index, id) {
      const selectedEmailsubType = this.emailSubscriptionTypes.find(
        emailSub => emailSub.id === id,
      )

      if (selectedEmailsubType) {
        let filter = {}
        if (selectedEmailsubType.filterable) {
          if (this.subsWithReport.includes(selectedEmailsubType.name)) {
            filter = { AirlineId: [], ReportNumber: [] }
          } else {
            filter = {
              AirlineId: [],
              MasterModelId: [],
              AnalyticId: [],
            }
          }
        }

        this.currentSubscriptions[index].emailType = selectedEmailsubType.name
        this.currentSubscriptions[index].name = this.formatSubNames(
          selectedEmailsubType.name,
        )
        this.currentSubscriptions[index].filterable =
          selectedEmailsubType.filterable
        this.currentSubscriptions[index].filter = filter
      }
    },
    handleSubscriptionRemove(event, subscription) {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }

      this.currentSubscriptions.splice(
        this.currentSubscriptions.findIndex(function (sub) {
          return sub.id === subscription.id
        }),
        1,
      )
    },
    validateSubscriptions() {
      this.$emit(
        'emailSubscriptionsValid',
        this.currentSubscriptions.every(subscription => {
          if (subscription.emailType && subscription.emailTypeId) {
            if (subscription.filterable) {
              if (subscription.filter !== {}) {
                let validity = Object.prototype.hasOwnProperty.call(
                  subscription.filter,
                  'AirlineId',
                )

                if (this.subsWithReport.includes(subscription.emailType)) {
                  validity = Object.prototype.hasOwnProperty.call(
                    subscription.filter,
                    'ReportNumber',
                  )
                } else {
                  validity =
                    Object.prototype.hasOwnProperty.call(
                      subscription.filter,
                      'MasterModelId',
                    ) &&
                    Object.prototype.hasOwnProperty.call(
                      subscription.filter,
                      'AnalyticId',
                    )
                }
                return validity
              } else return false
            } else {
              return true
            }
          }
          return false
        }),
      )
    },
    currentSubscriptionsChanged() {
      const subscriptions = this.currentSubscriptions.map(
        // eslint-disable-next-line no-unused-vars
        ({ filterable, name, ...currentSubscription }) => currentSubscription, // Remove irrelevanet properties
      )
      this.$emit('emailSubscriptionsChange', subscriptions)
    },
    formatSubNames(string) {
      return string ? string.match(/[A-Z][a-z]+|[A-Z]+|[0-9]+/g).join(' ') : ''
    },
  },
}
</script>

<style lang="scss">
.asc-email-subscriptions--container {
  .asc-accordion--container {
    .asc-accordion--item {
      > .asc-accordion--item--header {
        text-transform: unset;
        font-weight: normal;
        letter-spacing: 0;

        &:hover {
          color: $primary-black !important;

          .v-icon {
            color: $primary-black !important;
          }
        }

        &:not(.v-expansion-panel-header--mousedown):focus::before {
          opacity: 0;
        }
      }
    }
  }
}
</style>
